import { useRef, useEffect, useState } from 'react';

/**
 * Custom hook to detect if text is truncated in a container
 * @returns {Object} - { ref, isTruncated }
 */
export const useTextTruncation = () => {
  const ref = useRef<HTMLElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const checkTruncation = () => {
      // Check if the element's scroll width is greater than its client width
      // This indicates that the text is overflowing and being truncated
      const isOverflowing = element.scrollWidth > element.clientWidth;
      setIsTruncated(isOverflowing);
    };

    // Check on mount and when content changes
    checkTruncation();

    // Create a ResizeObserver to watch for size changes
    const resizeObserver = new ResizeObserver(checkTruncation);
    resizeObserver.observe(element);

    // Also listen for window resize events
    window.addEventListener('resize', checkTruncation);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkTruncation);
    };
  }, []);

  return { ref, isTruncated };
};

export default useTextTruncation;
